import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import TableSkeleton from "@/shared/components/shared/TableSkeleton";

// Import shared components
import { TransactionRow } from "./table-components/TransactionRow";

export default function TransactionTable({
  transactions = [],
  isLoading,
  onView,
  onRetry,
  onCancel,
  isQRCode = true,
  mixedTypes = false,
  showReseller = true,
  showMerchant = true,
  showProvider = true,
}) {
  const { t } = useLanguage() || {};
  const translate = (key) => (t ? t(key) : key);

  const columns = React.useMemo(() => {
    const cols = [
      {
        id: "transaction",
        header: translate("transaction"),
        width: "180px",
      },
      showReseller && {
        id: "reseller",
        header: translate("reseller"),
        className: "hidden md:table-cell",
      },
      showMerchant && {
        id: "merchant",
        header: translate("merchant"),
      },
      showProvider && {
        id: "provider",
        header: translate("provider"),
        className: "hidden lg:table-cell",
      },
      {
        id: "amount",
        header: translate("amount"),
        className: "text-center",
      },
      {
        id: "fee",
        header: "Commission Fee",
        className: "text-center",
      },
      {
        id: "net",
        header: translate("netAmount") || "Net Amount",
        className: "text-center",
      },
      {
        id: "currency",
        header: translate("currency") || "Currency",
        className: "text-center",
      },
      {
        id: "status",
        header: translate("status"),
      },
      {
        id: "date",
        header: translate("date"),
        className: "hidden sm:table-cell",
      },
      {
        id: "actions",
        header: "",
        className: "text-right",
      },
    ];
    return cols.filter(Boolean);
  }, [t, showReseller, showMerchant, showProvider]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <TableSkeleton columns={columns} rows={8} />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
      <div className="overflow-x-auto scrollbar-thin">
        <Table className="min-w-[800px]">
          <TableHeader>
            <TableRow className="bg-slate-50/50 border-b border-slate-100 hover:bg-transparent">
              {columns.map((col) => (
                <TableHead
                  key={col.id}
                  className={`h-11 py-0 font-bold text-slate-500 text-[11px] uppercase tracking-wider whitespace-nowrap ${col.className || ""}`}
                  style={col.width ? { width: col.width } : {}}
                >
                  {col.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {transactions.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="text-center py-20 text-slate-400 font-medium"
                >
                  <div className="flex flex-col items-center gap-2">
                    <p className="text-lg text-slate-300">No Transactions Found</p>
                    <p className="text-sm font-normal">
                      Try adjusting your filters or search criteria.
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              transactions.map((tx, index) => (
                <TransactionRow
                  key={tx.id || index}
                  tx={tx}
                  index={index}
                  onView={onView}
                  onRetry={onRetry}
                  onCancel={onCancel}
                  isQRCode={isQRCode}
                  mixedTypes={mixedTypes}
                  showReseller={showReseller}
                  showMerchant={showMerchant}
                  showProvider={showProvider}
                  t={t}
                  translate={translate}
                />
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
